// Personnel / Decision-Maker competence — single source of truth for authorisation enforcement.
// Used by the Personnel page (Auditors.js) and the office checkpoint (JobDetail.js). Keeps the
// "who may act as Decision-Maker" rule in ONE place so the UI badge and the approval gate never diverge.

export const DM_TRAINING_KEYS = [
  ['train_ct17_fundamentals', 'CT17 fundamentals'],
  ['train_checklist_interpretation', 'Checklist interpretation'],
  ['train_findings_submission', 'Findings & submission rules'],
  ['train_decision_making', 'Decision-making training'],
]

// Minimal, defensible competence-basis options (at least one required for authorisation).
export const COMPETENCE_BASIS_OPTIONS = [
  ['experience', 'Sufficient audit experience'],
  ['training', 'Relevant internal training completed'],
  ['qualification', 'Relevant qualification / equivalent'],
  ['management', 'Management-justified basis (see notes)'],
]

// What the Decision-Maker is authorised to approve (record at least one). Add future scopes here.
export const DM_SCOPE_OPTIONS = [
  ['rm_audit', 'bizSAFE RM Audit'],
  ['shms', 'SHMS Audit'],
  ['consass', 'SHMS Audit - Construction Worksite (ConSASS)'],
]
export function dmScopeLabel(k) { return (DM_SCOPE_OPTIONS.find(([x]) => x === k) || [, k])[1] }

export const COMPETENCY_EVENT_TYPES = [
  ['initial_training', 'Initial training'],
  ['annual_review', 'Annual competence review'],
  ['standards_update', 'Standards update'],
  ['process_update', 'Process update'],
  ['system_update', 'System update'],
  ['refresher', 'Refresher / retraining'],
]
export function eventTypeLabel(t) { return (COMPETENCY_EVENT_TYPES.find(([k]) => k === t) || [, t])[1] }

function basisArr(p) { return Array.isArray(p?.dm_competence_basis) ? p.dm_competence_basis : [] }

// Reasons a person is NOT yet an authorised Decision-Maker (job-independent).
// hasPendingMandatory = the person has at least one mandatory competency event not completed.
export function dmAuthorisationGaps(p, { hasPendingMandatory = false } = {}) {
  if (!p) return ['No person record']
  const g = []
  if (!p.active) g.push('Person is inactive')
  if (!p.is_decision_maker) g.push('Decision-Maker role not assigned')
  if (!p.dm_authorised) g.push('Decision-Maker authorisation not granted')
  if (!p.dm_approved_by || !p.dm_approved_date) g.push('Management approval (approved by + date) missing')
  const missing = DM_TRAINING_KEYS.filter(([k]) => !p[k]).map(([, l]) => l)
  if (missing.length) g.push('Internal training incomplete: ' + missing.join(', '))
  if (!basisArr(p).length) g.push('No competence basis recorded')
  if (!(Array.isArray(p.dm_scope) ? p.dm_scope : []).length) g.push('No approval scope recorded')
  if (p.retraining_required) g.push('Retraining required')
  if (hasPendingMandatory) g.push('Pending mandatory competency event')
  return g
}

export function isAuthorisedDecisionMaker(p, opts) {
  return dmAuthorisationGaps(p, opts).length === 0
}

// Job-specific gate: all of the above PLUS the person must not be the job's assigned auditor.
// assignedAuditorIds: array of auditor ids assigned to the job (caller resolves the job's auditor fields).
export function canActAsDecisionMaker(p, { assignedAuditorIds = [], hasPendingMandatory = false } = {}) {
  const gaps = dmAuthorisationGaps(p, { hasPendingMandatory })
  if (p && assignedAuditorIds.filter(Boolean).map(String).includes(String(p.id)))
    gaps.push('Cannot approve a job they were the assigned auditor for')
  return { ok: gaps.length === 0, gaps }
}

// Annual competence review prompt. Returns null if no due date set.
export function competenceReviewStatus(p, today = new Date()) {
  if (!p?.next_competence_review_due) return null
  const due = new Date(p.next_competence_review_due)
  const days = Math.ceil((due - today) / 86400000)
  if (days < 0) return { state: 'overdue', days, label: `Competence review overdue (${-days}d)` }
  if (days <= 30) return { state: 'due', days, label: `Competence review due in ${days}d` }
  return { state: 'ok', days, label: `Next review ${due.toLocaleDateString('en-SG')}` }
}

// From a list of {event_id, completed, mandatory} rows for ONE person, is there a pending mandatory gap?
export function hasPendingMandatory(rows) {
  return (rows || []).some(r => r.mandatory && !r.completed)
}
